// --- START OF FILE components/AddProjectModal.js ---
import React, { useState, useEffect, useMemo } from 'react';
import { X, Briefcase, User, Loader2 } from 'lucide-react';
import ClientSelector from './ClientSelector'; // Assuming ClientSelector exists and works

const AddProjectModal = ({ isOpen, onClose, onProjectAdd, apiBaseUrl, getAuthHeaders, clients, employees, circles, projects }) => {
  const [projectName, setProjectName] = useState('');
  const [projectNumber, setProjectNumber] = useState(''); // Maybe auto-generated?
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedOwnerId, setSelectedOwnerId] = useState('');
  const [selectedCircleId, setSelectedCircleId] = useState('');
  const [driveLink, setDriveLink] = useState('');
  const [slackLink, setSlackLink] = useState('');
  const [projectBrief, setProjectBrief] = useState('');
  const [heroImageUrl, setHeroImageUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const oldestRootCircleId = useMemo(() => {
    const list = circles || [];
    const roots = list.filter((circle) => circle?.parent_id === null || circle?.parent_id === undefined);
    if (!roots.length) return null;
    const sorted = [...roots].sort((a, b) => {
      const aTime = a.created_at ? new Date(a.created_at).getTime() : Number.MAX_SAFE_INTEGER;
      const bTime = b.created_at ? new Date(b.created_at).getTime() : Number.MAX_SAFE_INTEGER;
      if (aTime !== bTime) return aTime - bTime;
      return (a.id || 0) - (b.id || 0);
    });
    return sorted[0]?.id ?? null;
  }, [circles]);

  // Les cercles d'abord (Innowide, puis les plus utilisés par les projets : Projets, Myo6…), les rôles ensuite, avec
  // leur cercle entre parenthèses. Un rôle est un cercle feuille, comme sur la page Organisation.
  const circleGroups = useMemo(() => {
    const list = (circles || []).filter(Boolean);
    const byId = Object.fromEntries(list.map((circle) => [circle.id, circle]));
    const parents = new Set(list.map((circle) => circle.parent_id).filter((id) => id !== null && id !== undefined));
    const used = {};
    (projects || []).forEach((project) => {
      if (project?.circle_id) used[project.circle_id] = (used[project.circle_id] || 0) + 1;
    });
    const isRoot = (circle) => circle.parent_id === null || circle.parent_id === undefined;
    const order = (a, b) => (isRoot(b) - isRoot(a)) || ((used[b.id] || 0) - (used[a.id] || 0))
      || (a.name || '').localeCompare(b.name || '');
    const label = (circle) => circle.name || `Cercle #${circle.id}`;
    const isCircle = (circle) => isRoot(circle) || parents.has(circle.id);
    return {
      circles: list.filter(isCircle).sort(order).map((circle) => ({ id: circle.id, label: label(circle) })),
      roles: list.filter((circle) => !isCircle(circle)).sort(order).map((circle) => ({
        id: circle.id,
        label: byId[circle.parent_id] ? `${label(circle)} (${label(byId[circle.parent_id])})` : label(circle),
      })),
    };
  }, [circles, projects]);

  // Reset form when opening/closing
  useEffect(() => {
    if (isOpen) {
      setProjectName('');
      setProjectNumber(''); // Or fetch next available number?
      setSelectedClientId(null);
      setSelectedOwnerId('');
      setSelectedCircleId(oldestRootCircleId ? String(oldestRootCircleId) : '');
      setDriveLink('');
      setSlackLink('');
      setProjectBrief('');
      setHeroImageUrl('');
      setError('');
      setIsLoading(false);
    }
  }, [isOpen, oldestRootCircleId]);

  const handleClientSelection = (client) => {
      setSelectedClientId(client ? client.id : null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!projectName) {
      setError('Le nom du projet est requis.');
      return;
    }
    setError('');
    setIsLoading(true);

    const projectData = {
      name: projectName,
      project_number: projectNumber.trim() || null, // Send null if empty.
      client_id: selectedClientId,
      owner_id: selectedOwnerId ? parseInt(selectedOwnerId, 10) : null,
      circle_id: selectedCircleId ? parseInt(selectedCircleId, 10) : null,
      status: 'proposition', // Default status
      google_drive_url: driveLink.trim() ? driveLink.trim() : null,
      slack_channel_url: slackLink.trim() ? slackLink.trim() : null,
      brief: projectBrief.trim(),
      hero_image_url: heroImageUrl.trim() ? heroImageUrl.trim() : null,
    };

    try {
      await onProjectAdd(projectData); // Call the function passed from parent
      onClose(); // Close modal on success
    } catch (err) {
      console.error("Failed to add project:", err);
      setError(err.message || 'Erreur lors de la création du projet.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    // Modal backdrop
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      {/* Modal content */}
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
      >
        {/* Modal Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">Nouveau Projet</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100"
            disabled={isLoading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body - Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
            {/* Project Name */}
            <div>
              <label htmlFor="projectName" className="block text-sm font-medium text-gray-700 mb-1">
                Nom du Projet <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="projectName"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                required
                disabled={isLoading}
              />
            </div>

            {/* Project Number (Optional) */}
            <div>
              <label htmlFor="projectNumber" className="block text-sm font-medium text-gray-700 mb-1">
                Numéro de Projet (optionnel)
              </label>
              <input
                type="text"
                id="projectNumber"
                value={projectNumber}
                onChange={(e) => setProjectNumber(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                disabled={isLoading}
                placeholder="Ex: P24-001"
              />
            </div>

            {/* Project Brief */}
            <div>
              <label htmlFor="projectBrief" className="block text-sm font-medium text-gray-700 mb-1">
                Brief du projet
              </label>
              <textarea
                id="projectBrief"
                value={projectBrief}
                onChange={(e) => setProjectBrief(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm resize-none"
                placeholder="Décrivez en quelques lignes le pourquoi du projet et les bénéfices attendus"
                disabled={isLoading}
              />
            </div>

            {/* Hero Image */}
            <div>
              <label htmlFor="heroImageUrl" className="block text-sm font-medium text-gray-700 mb-1">
                Image de couverture (URL)
              </label>
              <input
                type="url"
                id="heroImageUrl"
                value={heroImageUrl}
                onChange={(e) => setHeroImageUrl(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="https://..."
                disabled={isLoading}
              />
              <p className="mt-1 text-xs text-gray-500">
                Facultatif. Utilisez une illustration ou une photo qui représente le projet.
              </p>
            </div>

            {/* Lien Google Drive */}
            <div>
              <label htmlFor="driveLink" className="block text-sm font-medium text-gray-700 mb-1">
                Lien Google Drive
              </label>
              <input
                type="url"
                id="driveLink"
                value={driveLink}
                onChange={(e) => setDriveLink(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="https://drive.google.com/..."
                disabled={isLoading}
              />
            </div>

            {/* Lien Slack */}
            <div>
              <label htmlFor="slackLink" className="block text-sm font-medium text-gray-700 mb-1">
                Lien Slack
              </label>
              <input
                type="url"
                id="slackLink"
                value={slackLink}
                onChange={(e) => setSlackLink(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="https://slack.com/app_redirect?channel=..."
                disabled={isLoading}
              />
            </div>

             {/* Client Selector */}
             <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                 {/* Assuming ClientSelector takes these props */}
                 <ClientSelector
                    // Pass necessary props, adjust based on ClientSelector's needs
                    // We don't need projectId here as we are creating a project
                    // currentClient={null} // No current client for new project
                    onClientChange={handleClientSelection}
                    clients={clients} // Pass fetched clients
                    // apiBaseUrl={apiBaseUrl} // If ClientSelector makes its own calls
                    // getAuthHeaders={getAuthHeaders} // If ClientSelector makes its own calls
                    isClearable={true} // Allow clearing selection
                    isDisabled={isLoading}
                 />
                 {/* Fallback simple select if ClientSelector is complex */}
                 {/* <select
                     value={selectedClientId || ''}
                     onChange={(e) => setSelectedClientId(e.target.value ? parseInt(e.target.value) : null)}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                     disabled={isLoading}
                 >
                     <option value="">-- Sélectionner un client --</option>
                     {clients.map(client => (
                         <option key={client.id} value={client.id}>{client.name}</option>
                     ))}
                 </select> */}
            </div>


            {/* Circle Selector */}
            <div>
              <label htmlFor="projectCircle" className="block text-sm font-medium text-gray-700 mb-1">
                Cercle
              </label>
              <select
                id="projectCircle"
                value={selectedCircleId}
                onChange={(e) => setSelectedCircleId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white"
                disabled={isLoading || circleGroups.circles.length + circleGroups.roles.length === 0}
              >
                {circleGroups.circles.length + circleGroups.roles.length === 0 ? (
                  <option value="">Aucun cercle disponible</option>
                ) : (
                  <>
                    <optgroup label="Cercles">
                      {circleGroups.circles.map((circle) => <option key={circle.id} value={circle.id}>{circle.label}</option>)}
                    </optgroup>
                    {circleGroups.roles.length > 0 && (
                      <optgroup label="Rôles">
                        {circleGroups.roles.map((role) => <option key={role.id} value={role.id}>{role.label}</option>)}
                      </optgroup>
                    )}
                  </>
                )}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Par défaut, le projet est rattaché au cercle racine. Les rôles viennent après les cercles.
              </p>
            </div>

            {/* Owner Selector */}
            <div>
              <label htmlFor="projectOwner" className="block text-sm font-medium text-gray-700 mb-1">
                Owner (Responsable)
              </label>
              <select
                id="projectOwner"
                value={selectedOwnerId}
                onChange={(e) => setSelectedOwnerId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white" // Added bg-white for consistency
                disabled={isLoading}
              >
                <option value="">-- Non assigné --</option>
                {employees && employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.name}</option>
                ))}
              </select>
            </div>

            {/* Error Message */}
            {error && (
              <p className="text-sm text-red-600">{error}</p>
            )}
          </div>

          {/* Modal Footer */}
          <div className="flex justify-end items-center p-4 bg-gray-50 border-t border-gray-200 space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading || !projectName}
              className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center"
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin h-4 w-4 mr-2" />
                  Création...
                </>
              ) : (
                'Créer Projet'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProjectModal;
// --- END OF FILE components/AddProjectModal.js ---
